// Kernel matching results -> the view models the could-help surface renders.
//
// bead hf-e5-availability-ccp.2.
//
// ── WHAT THIS MODULE IS NOT ALLOWED TO DECIDE ──────────────────────────────────
//
// WHO QUALIFIES. That is the kernel's `couldAskCandidates`, run server-side by
// `convex/domain/availability.ts:matchesForNeed`: a person appears only if one of
// their availability windows fully CONTAINS the need window, their help modes are
// compatible, and they have no commitment or transition conflict (§11, AVL-021).
// Matching is a yes-or-no property. There is no partial-overlap score, no "85% fit",
// and nothing here may invent one — the 2026-08-30 third review removed exactly that
// idea from the bead graph, and re-adding it in a browser would be the same bug in a
// worse place.
//
// ── WHAT IT DOES DECIDE: THE ORDER, AND ONLY THIS ORDER ────────────────────────
//
// The kernel returns candidates sorted by person id, which is deterministic and
// meaningless to a reader. Presentation order is a presentation concern, and the bead
// fixes it: freshness first, self-recorded before proxy-recorded.
//
// Both are about CONFIDENCE IN THE RECORD, not about who is a better helper. A note
// somebody wrote this morning is likelier to still be true than one from last week,
// and a person who recorded their own availability said it themselves. Neither is a
// ranking of people, which is why the copy never implies one.

import { instant, localDateOf, timeZone, type TimeZoneId } from "@herefor/domain-kernel";

import type { MatchCandidate } from "../../convex/matching";

/*
 * THE `DIRECT_REQUESTS_ENABLED` FLAG IS GONE, and its absence is the point.
 *
 * ccp.2 shipped "Ask [name]" as a stub behind a module constant, because the Direct
 * Request mutation did not exist yet and a button that silently did nothing would have
 * been worse than an honest "coming soon". `hf-e6-commitments-coverage-udt.1` built the
 * mutation, so the flag was removed rather than flipped to `true` — a permanently-true
 * flag is a branch nobody can reach and nobody dares delete.
 *
 * Whether the control appears is now decided by ONE thing, and it is server-decided:
 * `canAsk`, from the `request.send` capability on the caller's membership (AUT-007).
 */

/** How the availability reached the plan (AVL-011). */
export type AvailabilitySource = "self" | "proxy";

export interface CandidateView {
  readonly personId: string;
  readonly displayName: string;
  /**
   * DELIBERATELY ABSENT: the candidate's own availability window.
   *
   * Matching is full CONTAINMENT (§11, AVL-021), so every person on this list covers
   * the whole need window by definition. Printing "could help 09:00–17:00" beside a
   * 09:15–13:00 need adds a number the organiser cannot act on and invites the reading
   * that a longer window is a better match — which is exactly the partial-overlap
   * scoring the domain refuses to have. The need's own window is already on the screen
   * above this list.
   */
  /** When the availability was recorded, for the freshness line. */
  readonly recordedOnLabel: string;
  readonly recordedAt: number;
  readonly source: AvailabilitySource;
  /** Present only for a proxy record: who wrote it down, and from where. */
  readonly recordedByName: string | null;
  readonly sourceChannel: string | null;
  /**
   * This person has an open direct request on this need (REQ-003).
   *
   * Carried so the surface can show asked as visibly distinct from merely matched —
   * and, crucially, so it never reads as safer than it is. An asked candidate is still
   * only a question.
   */
  readonly asked: boolean;
}

export interface MatchingContext {
  readonly timeZone: string;
  /**
   * Ids of people with an open, unexpired ask on this need.
   *
   * Comes from `requests.requestsForNeed`'s `awaiting` list (bead
   * hf-e6-commitments-coverage-udt.1), which is the ONE place that decides what counts
   * as an open ask — `pending` AND not past its deadline, derived server-side on every
   * read. Matching by name rather than id would be cheaper and would go wrong the
   * first time two people in a circle shared one.
   */
  readonly askedPersonIds?: ReadonlySet<string>;
  /** Who recorded each proxy window, by person id. Names the plan already holds. */
  readonly namesByPersonId?: ReadonlyMap<string, string>;
}

/**
 * Candidates, ready to render, in the order an organiser should read them.
 *
 * The sort is stable on person id last, so two people whose availability was recorded
 * in the same millisecond do not swap places between renders.
 */
export function toCandidateViews(
  candidates: readonly MatchCandidate[],
  context: MatchingContext,
): readonly CandidateView[] {
  const zone = timeZone(context.timeZone);

  return candidates
    .map((candidate) => toCandidateView(candidate, zone, context))
    .slice()
    .sort(byFreshnessThenSelfRecorded);
}

export function toCandidateView(
  candidate: MatchCandidate,
  zone: TimeZoneId,
  context: MatchingContext,
): CandidateView {
  const recordedBy =
    candidate.recordedByPersonId === undefined
      ? null
      : (context.namesByPersonId?.get(String(candidate.recordedByPersonId)) ?? null);

  return {
    personId: String(candidate.personId),
    displayName: candidate.displayName,
    recordedOnLabel: localDateOf(instant(candidate.recordedAt), zone),
    recordedAt: candidate.recordedAt,
    source: candidate.source,
    recordedByName: recordedBy,
    sourceChannel: candidate.sourceChannel ?? null,
    asked: context.askedPersonIds?.has(String(candidate.personId)) ?? false,
  };
}

/**
 * Freshness first, then self-recorded before proxy-recorded, then stable by id.
 *
 * Deliberately NOT a quality ranking. Both keys describe how much to trust the
 * RECORD — a newer note is likelier to still hold, and somebody who entered their own
 * availability said it themselves — and neither says anything about who would be
 * better to ask. That distinction is why the copy beside this list offers no
 * "best match" and no bulk action.
 */
function byFreshnessThenSelfRecorded(left: CandidateView, right: CandidateView): number {
  if (left.recordedAt !== right.recordedAt) return right.recordedAt - left.recordedAt;
  if (left.source !== right.source) return left.source === "self" ? -1 : 1;
  return left.personId.localeCompare(right.personId);
}
